'use client';

import { useRouter } from 'next/navigation';
import { useCallback } from 'react';

import { useAuthStore } from '@/app/[locale]/auth/(presentation)/store/auth.store';
import type { EUserRole } from '@/app/[locale]/(dashboard)/dashboard/users/domain/enums/users.enum';
import { useNavigationGuard } from '../navigation.guard';
import { locales } from '@/i18n/config';

/**
 * Hook personalizado para manejar la navegación basada en roles
 */
export function useRoleNavigation() {
  const router = useRouter();
  const navigationGuard = useNavigationGuard();
  const { user, status } = useAuthStore();

  /**
   * Obtiene el rol más alto del usuario actual
   */
  const getCurrentUserRole = useCallback(() => {
    if (!user || !user.userRoles) {
      return null;
    }
    return navigationGuard.getHighestRole(user.userRoles, Boolean(user.organization?.id));
  }, [user, navigationGuard]);

  /**
   * Verifica si el usuario puede acceder a una ruta específica
   */
  const canAccessRoute = useCallback(
    (route: string) => {
      const currentRole = getCurrentUserRole();
      if (!currentRole) {
        return false;
      }

      // Normalizar ruta eliminando el locale si existe
      let normalizedRoute = route;
      const segments = route.split('/');
      // segments[0] is empty string because route starts with /
      // segments[1] might be the locale
      if (segments.length > 1 && locales.some((l) => l === segments[1])) {
        normalizedRoute = '/' + segments.slice(2).join('/');
        // Asegurar que la ruta raíz sea / si quedó vacía
        if (normalizedRoute === '/') normalizedRoute = '/dashboard'; // Ojo con esto, si es root
        if (normalizedRoute === '') normalizedRoute = '/';
      }

      return navigationGuard.canAccess(currentRole, normalizedRoute);
    },
    [getCurrentUserRole, navigationGuard]
  );

  /**
   * Navega a una ruta si el usuario tiene permisos
   */
  const navigateToRoute = useCallback(
    (route: string) => {
      if (canAccessRoute(route)) {
        router.push(route);
      } else {
        // Redirigir a la ruta por defecto del rol
        const currentRole = getCurrentUserRole();
        if (currentRole) {
          const defaultRoute = navigationGuard.getDefaultRedirect(currentRole);
          router.push(defaultRoute);
        }
      }
    },
    [canAccessRoute, router, getCurrentUserRole, navigationGuard]
  );

  /**
   * Navega a la ruta por defecto del rol del usuario
   */
  const navigateToDefault = useCallback(() => {
    const currentRole = getCurrentUserRole();
    if (currentRole) {
      const defaultRoute = navigationGuard.getDefaultRedirect(currentRole);
      router.push(defaultRoute);
    }
  }, [getCurrentUserRole, navigationGuard, router]);

  /**
   * Obtiene el menú de navegación del usuario actual
   */
  const getCurrentUserMenu = useCallback(() => {
    const currentRole = getCurrentUserRole();
    if (!currentRole) {
      return [];
    }
    return navigationGuard.getNavigationMenu(currentRole);
  }, [getCurrentUserRole, navigationGuard]);

  /**
   * Verifica si el usuario está autenticado
   */
  const isAuthenticated = status === 'authenticated';

  /**
   * Verifica si el usuario tiene un rol específico
   */
  const hasRole = useCallback(
    (role: string) => {
      return user?.userRoles?.includes(role) || false;
    },
    [user]
  );

  /**
   * Verifica si el usuario tiene al menos uno de los roles especificados
   */
  const hasAnyRole = useCallback(
    (roles: string[]) => {
      if (!user?.userRoles) {
        return false;
      }
      return roles.some((role) => user.userRoles.includes(role));
    },
    [user]
  );

  /**
   * Verifica si el usuario tiene todos los roles especificados
   */
  const hasAllRoles = useCallback(
    (roles: string[]) => {
      if (!user?.userRoles) {
        return false;
      }
      return roles.every((role) => user.userRoles.includes(role));
    },
    [user]
  );

  return {
    // Estado
    isAuthenticated,
    currentUser: user,
    currentRole: getCurrentUserRole(),

    // Navegación
    canAccessRoute,
    navigateToRoute,
    navigateToDefault,
    getCurrentUserMenu,

    // Verificación de roles
    hasRole,
    hasAnyRole,
    hasAllRoles,

    // Utilidades
    getAllowedRoutes: (role: string) => navigationGuard.getAllowedRoutes(role as EUserRole),
    getDefaultRedirect: (role: string) => navigationGuard.getDefaultRedirect(role as EUserRole),
  };
}
